/**
 * Icono para marcadores de propiedades listadas. Nunca devolvemos null:
 * todas las propiedades arrancan como círculo naranja (no el pin rojo default
 * de Google). El pin rojo lo reservamos para la ubicación del cliente.
 *
 *   default  → naranja         #FF9800
 *   selected → naranja intenso #FF5722 (más grande, borde más grueso)
 *   visited  → azul            #2196F3
 */
export const getMarkerIcon = (isLoaded, isSelected, isVisited) => {
  if (!isLoaded) return null;
  if (isSelected) {
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: "#FF5722",
      fillOpacity: 1,
      strokeWeight: 3,
      strokeColor: "#ffffff",
      scale: 12,
    };
  }
  if (isVisited) {
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: "#2196F3",
      fillOpacity: 1,
      strokeWeight: 2,
      strokeColor: "#ffffff",
      scale: 9,
    };
  }
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: "#FF9800",
    fillOpacity: 0.95,
    strokeWeight: 2,
    strokeColor: "#ffffff",
    scale: 9,
  };
};

export const generateCircleCoords = (center, radius) => {
  const circleCoords = [];
  for (let i = 0; i <= 360; i += 5) {
    const point = window.google.maps.geometry.spherical.computeOffset(
      center,
      radius,
      i
    );
    circleCoords.push(point);
  }
  return circleCoords;
};

/**
 * Dibuja un polígono circular en el mapa y devuelve la instancia del Polygon.
 * - map: instancia del Google Map
 * - centerLatLng: LatLngLiteral o LatLng
 * - radius: en metros
 */
export const drawCircleOnMap = (map, centerLatLng, radius = 1000, opts = {}) => {
  if (!map || !window.google?.maps) return null;
  const center = (centerLatLng instanceof window.google.maps.LatLng)
    ? centerLatLng
    : new window.google.maps.LatLng(centerLatLng.lat, centerLatLng.lng);

  const circleCoords = generateCircleCoords(center, radius);
  const path = circleCoords.map(p => ({ lat: p.lat(), lng: p.lng() }));

  const polygon = new window.google.maps.Polygon({
    paths: path,
    strokeColor: opts.strokeColor || "#FF0000",
    strokeOpacity: opts.strokeOpacity || 0.8,
    strokeWeight: opts.strokeWeight || 2,
    fillColor: opts.fillColor || "#FF0000",
    fillOpacity: opts.fillOpacity ?? 0.12,
  });

  polygon.setMap(map);
  return polygon;
};

/**
 * Centra el mapa en `place` y dibuja un polígono circular con el radius pasado.
 * - map: instancia Google Map
 * - place: objeto place de Google (tiene geometry.location)
 * - radius: metros
 * - onPolygonComplete: callback con la polygon instance
 */
export const centerMapAndDrawPolygon = (map, place, radius = 1000, onPolygonComplete) => {
  if (!place?.geometry?.location || !map || !window.google?.maps) return;
  const center = place.geometry.location;
  if (!center) return;

  // Si hay un polygon previo en el mapa lo dejamos que lo borre el caller (map mantiene referencia).
  const newPolygon = drawCircleOnMap(map, center, radius, {
    strokeColor: "#FF0000",
    fillColor: "#FF0000",
    fillOpacity: 0.1,
  });

  // Ajustar bounds
  const bounds = new window.google.maps.LatLngBounds();
  const circleCoords = generateCircleCoords(center, radius);
  circleCoords.forEach((coord) => bounds.extend(coord));
  map.fitBounds(bounds);

  onPolygonComplete?.(newPolygon);
};
